/**
 * Shared Error Codes
 *
 * Provides typed error codes for consistent error handling across
 * backend and frontend. Enables specific error handling and
 * user-friendly error messages.
 */

/**
 * Authentication error codes
 */
export const AUTH_ERROR_CODES = {
  /** No authentication token provided */
  AUTH_REQUIRED: "AUTH_REQUIRED",
  /** Token format is invalid */
  AUTH_INVALID_TOKEN: "AUTH_INVALID_TOKEN",
  /** Token has expired */
  AUTH_TOKEN_EXPIRED: "AUTH_TOKEN_EXPIRED",
  /** Firebase token valid but user not registered in database */
  AUTH_USER_NOT_REGISTERED: "AUTH_USER_NOT_REGISTERED",
  /** Firebase verification failed */
  AUTH_FIREBASE_ERROR: "AUTH_FIREBASE_ERROR",
  /** Token getter not initialized (frontend) */
  AUTH_NOT_INITIALIZED: "AUTH_NOT_INITIALIZED",
  /** User not signed in (frontend) */
  AUTH_NO_TOKEN: "AUTH_NO_TOKEN",
  /** Request should be retried after user registration */
  AUTH_RETRY_AFTER_REGISTER: "AUTH_RETRY_AFTER_REGISTER",
  /** Account pending admin approval / disabled */
  AUTH_ACCOUNT_DISABLED: "AUTH_ACCOUNT_DISABLED",
  /** CSRF token missing or invalid on a session-cookie request */
  AUTH_CSRF_INVALID: "AUTH_CSRF_INVALID",
} as const;

export type AuthErrorCode =
  typeof AUTH_ERROR_CODES[keyof typeof AUTH_ERROR_CODES];

/**
 * API error codes
 *
 * Canonical superset of every non-auth `code` the backend emits on the wire.
 * The backend `ErrorCode` enum (backend/src/errors/custom-errors.ts) is bound
 * to this contract by a compile-time subset check, so the codes the frontend
 * localizes against (errors:codes.*) can never silently drift from what
 * AppError actually throws (pirx-kg89.10).
 */
export const API_ERROR_CODES = {
  /** Resource not found */
  NOT_FOUND: "NOT_FOUND",
  /** Resource state conflicts with requested operation */
  CONFLICT: "CONFLICT",
  /** A one-time action (e.g. onboarding) has already been completed */
  ALREADY_COMPLETED: "ALREADY_COMPLETED",
  /** Validation error */
  VALIDATION_ERROR: "VALIDATION_ERROR",
  /** Request body / amount failed a value-level check */
  INVALID_AMOUNT: "INVALID_AMOUNT",
  /** Initial balance value is invalid */
  INVALID_INITIAL_BALANCE: "INVALID_INITIAL_BALANCE",
  /** Request is authenticated but not authorized */
  FORBIDDEN: "FORBIDDEN",
  /** Legacy unauthorized error code still returned by some endpoints */
  UNAUTHORIZED: "UNAUTHORIZED",
  /** Account has insufficient credit balance for the operation */
  INSUFFICIENT_BALANCE: "INSUFFICIENT_BALANCE",
  /** Rate limit exceeded */
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  /** Destructive action requires explicit confirmation */
  CONFIRMATION_REQUIRED: "CONFIRMATION_REQUIRED",
  /** Request timed out */
  TIMEOUT: "TIMEOUT",
  /** Request payload exceeds the allowed size */
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  /** Internal server error */
  INTERNAL_ERROR: "INTERNAL_ERROR",
  /** An upstream/external service failed */
  EXTERNAL_SERVICE_ERROR: "EXTERNAL_SERVICE_ERROR",
  /** Service unavailable */
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
} as const;

export type ApiErrorCode = typeof API_ERROR_CODES[keyof typeof API_ERROR_CODES];

/**
 * All error codes combined
 */
export const ERROR_CODES = {
  ...AUTH_ERROR_CODES,
  ...API_ERROR_CODES,
} as const;

export type ErrorCode = AuthErrorCode | ApiErrorCode;

/**
 * Structured error response from API
 */
export interface ApiErrorResponse {
  /** Short error description */
  error: string;
  /** Machine-readable error code */
  code: ErrorCode;
  /** User-friendly message */
  message: string;
  /** Additional error details (optional) */
  details?: Record<string, unknown>;
  /**
   * URL-scan fail-loud taxonomy code (pirx-27tn), present only on
   * `POST /api/audits/scan-url` failures. The client surfaces it so the UI can
   * render the specific state; absent on all other errors.
   */
  failureCode?: string;
}

/**
 * Check if a value is a valid error code
 */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && Object.hasOwn(ERROR_CODES, value);
}

/**
 * Check if a response is an ApiErrorResponse
 */
export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.error === "string" &&
    typeof obj.code === "string" &&
    isErrorCode(obj.code) &&
    typeof obj.message === "string"
  );
}
